import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { API_BASE_URL } from '../../application/ports/api-base-url.token';
import { AssetRepositoryPort } from '../../application/ports/asset-repository.port';
import { ROOT_ASSET_OBIDS } from '../../application/ports/root-asset-obids.token';
import { AssetType } from '../../domain/models/asset-type.enum';
import { Asset, AssetRelationship } from '../../domain/models/asset.model';
import { AssetInstanceDetailDto } from '../models/asset-instance-detail.dto';
import { AssetInstanceRelationshipDto } from '../models/asset-instance-relationship.dto';
import { UpdateAssetPropertyResponse } from '../models/update-asset-property-response.model';

@Injectable()
export class AssetInstancesApiAdapter implements AssetRepositoryPort {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly rootObids = inject(ROOT_ASSET_OBIDS);
  private readonly _dtoCache = new Map<string, AssetInstanceDetailDto>();

  async loadAll(): Promise<Asset[]> {
    const results = await Promise.allSettled(
      this.rootObids.map(async (obid) => {
        const dto = await this._fetchInstance(obid);
        return dto ? this._toAsset(obid, null, dto) : null;
      }),
    );
    return results
      .filter(
        (r): r is PromiseFulfilledResult<Asset | null> =>
          r.status === 'fulfilled',
      )
      .map((r) => r.value)
      .filter((a): a is Asset => a !== null);
  }

  async loadChildren(parentId: string): Promise<Asset[]> {
    const dto = await this._fetchInstance(parentId);
    if (!dto) return [];
    return dto.relationships
      .map((rel) => this._relationshipToAsset(rel, parentId))
      .filter((a): a is Asset => a !== null);
  }

  async loadById(id: string): Promise<Asset | null> {
    const dto = await this._fetchInstance(id);
    if (!dto) return null;
    return this._toAsset(id, null, dto);
  }

  async search(query: string): Promise<Asset[]> {
    return [];
  }

  updatePropertyValue(
    obid: string,
    propertyName: string,
    value: unknown,
  ): Observable<UpdateAssetPropertyResponse> {
    return this.http.patch<UpdateAssetPropertyResponse>(
      `${this.baseUrl}/api/asset-instances/${obid}/properties`,
      { propertyName, value },
    );
  }

  private async _fetchInstance(
    obid: string,
  ): Promise<AssetInstanceDetailDto | null> {
    if (this._dtoCache.has(obid)) {
      return this._dtoCache.get(obid)!;
    }
    try {
      const dto = await firstValueFrom(
        this.http.get<AssetInstanceDetailDto>(
          `${this.baseUrl}/api/asset-instances/${obid}`,
        ),
      );
      this._dtoCache.set(obid, dto);
      return dto;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return null;
      }
      throw this._handleError(error, `Failed to fetch asset instance ${obid}`);
    }
  }

  private _toAsset(
    obid: string,
    parentId: string | null,
    dto: AssetInstanceDetailDto,
  ): Asset {
    const rawName =
      (dto.properties['name'] as string) ??
      (dto.properties['object_name'] as string) ??
      '';
    const label = rawName;
    const type = this._mapAssetType(dto.entityType);
    const hasChildren = dto.relationshipsCount > 0;

    const relationships: AssetRelationship[] = dto.relationships.map((rel) => ({
      id: String(rel.connectedAsset['obid'] ?? ''),
      name: String(
        rel.connectedAsset['name'] ?? rel.connectedAsset['object_name'] ?? '',
      ),
      entityType: String(rel.connectedAsset['connectedAssetEntityType'] ?? ''),
      relationshipType: rel.relationshipType,
    }));

    const filteredProperties = Object.fromEntries(
      Object.entries(dto.properties).filter(
        ([k, v]) =>
          v !== null && !k.startsWith('_') && k !== 'labels' && k !== 'oid',
      ),
    );

    return new Asset(obid, label, type, dto.entityType, parentId, [], {
      properties: { ...filteredProperties, _hasChildren: hasChildren },
      relationships,
    });
  }

  private _relationshipToAsset(
    rel: AssetInstanceRelationshipDto,
    parentId: string,
  ): Asset | null {
    const ca = rel.connectedAsset;
    const obid = this._extractObid(ca);
    if (!obid) return null;

    const rawName =
      (ca['name'] as string) ?? (ca['object_name'] as string) ?? '';
    const label = rawName;
    const entityType = String(ca['connectedAssetEntityType'] ?? '');
    const type = this._mapAssetType(entityType);

    const filteredProperties = Object.fromEntries(
      Object.entries(ca).filter(
        ([k, v]) =>
          v !== null &&
          !k.startsWith('_') &&
          k !== 'labels' &&
          k !== 'oid' &&
          k !== 'connectedAssetEntityType' &&
          k !== 'connectedRelationshipsCount',
      ),
    );

    const hasChildren = Number(ca['connectedRelationshipsCount'] ?? 0) > 0;
    return new Asset(obid, label, type, entityType, parentId, [], {
      properties: { ...filteredProperties, _hasChildren: hasChildren },
    });
  }

  private _extractObid(connectedAsset: Record<string, unknown>): string | null {
    const raw = connectedAsset['obid'];
    if (raw == null) return null;
    const str = String(raw);
    return str && str !== 'null' && str !== 'undefined' ? str : null;
  }

  private _mapAssetType(entityType: string): AssetType {
    const lower = entityType.toLowerCase();
    if ((Object.values(AssetType) as string[]).includes(lower)) {
      return lower as AssetType;
    }
    return AssetType.UNKNOWN;
  }

  private _handleError(error: unknown, defaultMessage: string): Error {
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message || error.message || defaultMessage;
      return new Error(`${defaultMessage}: ${message}`);
    }
    if (error instanceof Error) return error;
    return new Error(defaultMessage);
  }
}
